import { AutoFormFields } from 'lib/LegacyForm';
import { Option } from 'lib/types';
import { ContentModule, ContentModuleType, IDisplay } from '../models/ContentModule';
import {
    createModuleAwardsForm,
    transformAwardsFormFieldToModuleFields,
    transformModuleToAwardsFormFields
} from './createModuleAwardsForm';
import {
    createModuleBasicForm,
    transformBasicFormFieldToModuleFields,
    transformModuleToBasicFormFields
} from './createModuleBasicForm';
import {
    createModuleLocationsForm,
    transformLocationFormFieldToModuleFields,
    transformModuleToLocationFormFields
} from './createModuleLocationsForm';
import {
    createModuleLoyaltyBalanceForm,
    transformLoyaltyBalanceFormFieldToModuleFields,
    transformModuleToLoyaltyBalanceFormFields
} from './createModuleLoyaltyBalanceForm';
import {
    createModuleMarketingForm,
    transformMarketingFormFieldToModuleFields,
    transformModuleToMarketingFormFields
} from './createModuleMarketingForm';
import { createModulePrimaryActionForm } from './createModulePrimaryActionForm';

export interface ContentModuleAdditionalData {
    display?: IDisplay;
    perkOptions?: Option[];
    selectedIncludePerkIds?: Option[];
    selectedExcludePerkIds?: Option[];
    locationOptions?: Option[];
    segmentOptions?: Option[];
    selectedSegmentIds?: Option[];
    selectedExcludingSegmentIds?: Option[];
}

const moduleForms: (additionalData?: ContentModuleAdditionalData) => {
    [key in ContentModuleType]: AutoFormFields;
} = additionalData => ({
    PRIMARY_ACTION: createModulePrimaryActionForm,
    MARKETING: createModuleMarketingForm({
        display: additionalData?.display,
        locationOptions: additionalData?.locationOptions,
        segmentOptions: additionalData?.segmentOptions,
        selectedSegmentIds: additionalData?.selectedSegmentIds,
        selectedExcludingSegmentIds: additionalData?.selectedExcludingSegmentIds
    }),
    LOCATIONS: createModuleLocationsForm,
    FAVOURITES: createModuleBasicForm,
    AWARDS: createModuleAwardsForm({
        perkOptions: additionalData?.perkOptions,
        selectedIncludePerkIds: additionalData?.selectedIncludePerkIds,
        selectedExcludePerkIds: additionalData?.selectedExcludePerkIds
    }),
    SECRET_DJ: createModuleBasicForm,
    LOYALTY_BALANCE: createModuleLoyaltyBalanceForm
});

export const createModuleForm = (
    moduleType: ContentModuleType,
    additionalData?: ContentModuleAdditionalData
) => moduleForms(additionalData)[moduleType];

export function transformModuleToFormFields(
    module: ContentModule,
    additionalData?: ContentModuleAdditionalData
): any {
    switch (module.type) {
        case ContentModuleType.FAVOURITES:
            return transformModuleToBasicFormFields(module);
        case ContentModuleType.AWARDS:
            return transformModuleToAwardsFormFields(module, additionalData?.perkOptions);
        case ContentModuleType.LOCATIONS:
            return transformModuleToLocationFormFields(module);
        case ContentModuleType.MARKETING:
            return transformModuleToMarketingFormFields(module, {
                locationOptions: additionalData?.locationOptions,
                segmentOptions: additionalData?.segmentOptions
            });
        case ContentModuleType.LOYALTY_BALANCE:
            return transformModuleToLoyaltyBalanceFormFields(module);
        default:
            return module;
    }
}

export function transformFormFieldToModuleFields(
    module: ContentModule,
    fieldName: string,
    value: any,
    formData: { [key: string]: any }
): { name: string; value: any }[] {
    if (fieldName === 'index') {
        return [{ name: fieldName, value: Number(value) }];
    }
    const scheduleFields = ['schedule', 'visibleTo', 'visibleFrom'];
    if (scheduleFields.includes(fieldName)) {
        return [{ name: fieldName, value }];
    }
    switch (module.type) {
        case ContentModuleType.FAVOURITES:
            return transformBasicFormFieldToModuleFields(fieldName, value, formData);
        case ContentModuleType.AWARDS:
            return transformAwardsFormFieldToModuleFields(fieldName, value, formData);
        case ContentModuleType.LOCATIONS:
            return transformLocationFormFieldToModuleFields(fieldName, value, formData);
        case ContentModuleType.MARKETING:
            return transformMarketingFormFieldToModuleFields(fieldName, value, formData);
        case ContentModuleType.LOYALTY_BALANCE:
            return transformLoyaltyBalanceFormFieldToModuleFields(fieldName, value, formData);
        default:
            return [{ value, name: fieldName }];
    }
}
